<script setup lang="ts">
  import { Calendar, CalendarSize, type DateISO, type CalendarDateRange } from '@travelata-libs/ui-kit';
  import { useCloseOnEscape } from '@/entities/searchForm';
  import MorphPopoverLayer from '@/shared/ui/MorphPopoverLayer.vue';
  import DateFromFlexChips from './DateFromFlexChips.vue';

  interface DateFromDropdownProps {
    anchorIso?: string;
    flexibleForCalendar?: number | null;
    flexible: number;
    minDate: string;
    maxDate: string;
    disabledFromDate?: string | null;
    visible: boolean;
    range?: boolean;
    rangeValue?: CalendarDateRange<DateISO> | null;
    withExactOption?: boolean;
    dropdownTarget?: HTMLElement | null;
  }

  const props = withDefaults(defineProps<DateFromDropdownProps>(), {
    anchorIso: '',
    flexibleForCalendar: null,
    range: false,
    rangeValue: null,
    withExactOption: false,
    disabledFromDate: null,
    dropdownTarget: null,
  });

  interface DateFromDropdownEmits {
    (e: 'date-click', iso: string): void;
    (e: 'day-click', iso: string): void;
    (e: 'day-hover', iso: string | null): void;
    (e: 'select-flexible', n: number): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<DateFromDropdownEmits>();

  const onDateClick = (payload: DateISO | CalendarDateRange<DateISO> | null): void => {
    if (props.range) {
      return;
    }
    if (typeof payload === 'string') {
      emit('date-click', payload);
    } else if (payload?.start) {
      emit('date-click', payload.start);
    }
  };

  const onDayClick = (iso: string): void => {
    if (props.range) {
      emit('day-click', iso);
    }
  };

  const onDayHover = (iso: string | null): void => {
    if (props.range) {
      emit('day-hover', iso);
    }
  };

  const onSelectFlexible = (n: number) => {
    emit('select-flexible', n);
  };

  useCloseOnEscape(
    () => props.visible,
    () => emit('close'),
  );
</script>

<template>
  <MorphPopoverLayer
    :target="dropdownTarget"
    :visible="visible"
  >
    <div
      class="date-from-dropdown"
      data-testid="date-from-dropdown"
    >
      <Calendar
        data-testid="date-from-calendar"
        :model-value="range ? rangeValue : anchorIso"
        double
        :size="CalendarSize.M"
        :range="range"
        :flexible-days="range ? null : flexibleForCalendar"
        :with-footer="false"
        :min-date="minDate"
        :max-date="maxDate"
        :disabled-from-date="disabledFromDate"
        @date-click="onDateClick"
        @day-click="onDayClick"
        @day-hover="onDayHover"
      />

      <DateFromFlexChips
        :value="flexible"
        :with-exact-option="withExactOption"
        @select="onSelectFlexible"
      />
    </div>
  </MorphPopoverLayer>
</template>

<style scoped lang="scss">
  .date-from-dropdown {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-l);
    width: max-content;
  }
</style>
